import React, { useState, useMemo } from "react";
import { Tooltip } from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
} from "@mui/material";
import { TableSortLabel } from "@mui/material";
import Loader from "../assets/Loader.png";
import LoaderAnimation from "../assets/LoaderAnimation.png";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import "../App.css";

const TableDataContainer = (props) => {
  const {
    columnDefs,
    filteredRowData1,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    toggleRow,
    selectedRows,
    toggleRowSelection,
    selectedToggle,
    loader,
    tableFilter,
    error,
    iError,
    selectedMeasurement,
  } = props;
  const [orderBy, setOrderBy] = useState("efficiency"); // default sort column
  const [order, setOrder] = useState("desc"); // default sort order

  const columnDefsForPressure = useMemo(
    () => [
      {
        headerName: "Series",
        field: "series",
      },
      {
        headerName: "Displacement",
        field: "displacement",
      },
      { headerName: "Quick selector link", field: "dummyField" },
      {
        headerName: "Torque",
        field: "torque",
      },
      {
        headerName: "Speed",
        field: "speed",
      },
      {
        headerName: "Valve type",
        field: "valveType",
      },
      {
        headerName: "Gearset type",
        field: "gearsetType",
      },
      {
        headerName: "Bearing type",
        field: "bearingType",
      },
      {
        headerName: "Bearing Capacity",
        field: "bearingCapacity",
      },
      // {
      //   headerName: "TPI (%)",
      //   field: "durabilityIndex",
      // },
      {
        headerName: "Eff. (%)",
        field: "efficiency",
      },
    ],
    []
  );

  const columnsToUse = useMemo(() => {
    let baseColumns;
    baseColumns =
      selectedToggle === "FlowPressure" ? columnDefsForPressure : columnDefs;
    return baseColumns.map((ele) => {
      let updatedHeader = ele.headerName;
      let headerLabel = ele.headerName;
      if (selectedMeasurement === "Imperial") {
        if (selectedToggle === "TorqueSpeed") {
          if (ele.headerName === "Pressure")
            updatedHeader = "Required pressure (psi)";
          if (ele.headerName === "Flow")
            updatedHeader = "Required flow (gal/min)";
        } else if (selectedToggle === "FlowPressure") {
          if (ele.headerName === "Torque") updatedHeader = "Torque (lbf-in)";
          if (ele.headerName === "Speed") updatedHeader = "Speed (RPM)";
        }
        if (ele.headerName === "Bearing Capacity")
          updatedHeader = "Bearing capacity (lbf)";
      } else if (selectedMeasurement === "Metric") {
        if (selectedToggle === "TorqueSpeed") {
          if (ele.headerName === "Pressure")
            updatedHeader = "Required pressure (bar)";
          if (ele.headerName === "Flow")
            updatedHeader = "Required flow (l/min)";
        } else if (selectedToggle === "FlowPressure") {
          if (ele.headerName === "Torque") updatedHeader = "Torque (Nm)";
          if (ele.headerName === "Speed") updatedHeader = "Speed (RPM)";
        }
        if (ele.headerName === "Bearing Capacity")
          updatedHeader = "Bearing capacity (N)";
      }
      if (ele.headerName === "Bearing Type") {
        updatedHeader = (
          <div>
            Bearing
            <br />
            type
          </div>
        );
        headerLabel = "Bearing Type"; // ALWAYS PLAIN TEXT
      }
      if (ele.field === "dummyField") {
        return {
          ...ele,
          headerName: updatedHeader,
          headerLabel,
          sortable: false,
        };
      }

      return {
        ...ele,
        headerName: updatedHeader,
        headerLabel:
          typeof ele.headerName === "string" ? ele.headerName : "Column",
      };
    });
  }, [selectedToggle, columnDefsForPressure, columnDefs, selectedMeasurement]);

  const handleRowSelection = (row, isSelected) => {
    toggleRowSelection(row, isSelected);
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      // Toggle sort direction
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      // Set new column and default to ascending
      setOrderBy(field);
      setOrder("asc");
    }
  };
  const filteredData = useMemo(() => {
    const fieldMap = {
      valveSystem: "valveType",
      gearwheelSet: "gearsetType",
      radialBearingLoad: "bearingCapacity",
    };
    if (!tableFilter || typeof tableFilter !== "object")
      return filteredRowData1;
    const activeFilters = Object.entries(tableFilter).filter(
      ([_, value]) => value !== "" && value !== null && value !== undefined
    );
    return filteredRowData1.filter((item) => {
      return activeFilters.every(([key, value]) => {
        const field = fieldMap[key] || key;
        if (field === "bearingCapacity") return item[field] >= value;
        return item[field].toLowerCase() === value.toLowerCase();
      });
    });
  }, [filteredRowData1, tableFilter]);

  const stickyStyles = {
    position: "sticky",
    fontWeight: "bold",
    backgroundColor: "#fff",
    border: "0.5px solid", // ✅ updated from borderBottom
    padding: "10px",
    textAlign: "center",
    fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
    height: "30px", // ✅ fixed height
    minWidth: "10px", // ✅ fixed width
    maxWidth: "120px",
  };

  const stickyStyles2 = {
    position: "sticky",
    textAlign: "center",
    padding: "10px",
    fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
    height: "30px", // ✅ match header height
    minWidth: "10px", // ✅ match header width
    maxWidth: "10px",
  };

  return loader ? (
    <div className="loader-container">
      <div className="dot-loader">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
        {/* <img src={Loader} alt="dot1" />
        <img src={LoaderAnimation} alt="dot2" />
        <img src={Loader} alt="dot3" /> */}
      </div>
    </div>
  ) : iError || error || !filteredData || filteredData.length === 0 ? (
    <div
      style={{
        textAlign: "center",
        padding: "2rem",
        fontSize: "1.2rem",
        color: "#555",
        fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
      }}
    >
      No Data to Show
    </div>
  ) : (
    <>
      <div style={{ width: 701 }}>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "none",
            border: "1px solid #ddd",
            maxHeight: "450px",
            overflow: "auto", // allows scroll if needed
            // width: "100%", // use full width of parent container
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#fff",
                    border: "0.5px solid", // ✅ updated from borderBottom
                    padding: "5px",
                    textAlign: "center",
                    fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                    height: "30px", // ✅ fixed height
                    minWidth: "40px", // ✅ fixed width
                    maxWidth: "40px",
                    ...stickyStyles,
                  }}
                >
                  Compare function diagram
                </TableCell>
                <TableCell
                  sx={{ ...stickyStyles, cursor: "pointer" }}
                  onClick={() => handleSort("series")}
                >
                  Series
                </TableCell>
                <TableCell
                  sx={{ ...stickyStyles, cursor: "pointer" }}
                  onClick={() => handleSort("displacement")}
                >
                  Displ.
                </TableCell>
                {columnsToUse.slice(2).map((col) => (
                  <TableCell
                    key={col.field}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#fff",
                      border: "0.5px solid", // ✅ updated from borderBottom
                      padding: "5px",
                      textAlign: "center",
                      fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                      height: "30px", // ✅ fixed height
                      minWidth: "10px", // ✅ fixed width
                      maxWidth: "120px",
                    }}
                  >
                    <Tooltip
                      title={
                        col.sortable === false
                          ? ""
                          : `Sort by ${col.headerLabel}`
                      }
                      arrow
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          width: "100%",
                          cursor: "pointer",
                        }}
                        onClick={() => handleSort(col.field)}
                      >
                        {col.headerName}
                        {/* <TableSortLabel
                          active={orderBy === col.field}
                          direction={orderBy === col.field ? order : "asc"}
                          onClick={() => handleSort(col.field)}
                        >
                          {col.headerName}
                        </TableSortLabel> */}
                      </Box>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...filteredData]
                .sort((a, b) => {
                  const aValue = a[orderBy];
                  const bValue = b[orderBy];

                  if (aValue === undefined || bValue === undefined) return 0;

                  const parseValue = (val) =>
                    typeof val === "string" && val.endsWith("%")
                      ? parseFloat(val)
                      : typeof val === "string"
                      ? parseFloat(val)
                      : val;

                  const aParsed = parseValue(aValue);
                  const bParsed = parseValue(bValue);

                  if (isNaN(aParsed) || isNaN(bParsed)) {
                    // fallback string comparison
                    return order === "asc"
                      ? String(aValue).localeCompare(String(bValue))
                      : String(bValue).localeCompare(String(aValue));
                  }

                  return order === "asc"
                    ? aParsed - bParsed
                    : bParsed - aParsed;
                })
                // .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    <TableRow
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "#f4f4f4",
                        }, // Light gray for odd rows
                        "&:nth-of-type(even)": {
                          backgroundColor: "#fff",
                        }, // White for even rows
                        // "&:hover": { backgroundColor: "#f1f1f1" },
                        cursor: "pointer",
                        padding: "5px",
                        fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                      }}
                      onClick={() => toggleRow(rowIndex)} // Toggle the row expansion
                    >
                      <TableCell
                        sx={{
                          ...stickyStyles2,
                        }}
                      >
                        {(() => {
                          const isChecked = (selectedRows ?? []).some((r) => {
                            if (selectedToggle === "FlowPressure") {
                              return (
                                r.series === row.series &&
                                r.displacement === row.displacement &&
                                r.pressure === row.pressure
                              );
                            } else {
                              return (
                                r.series === row.series &&
                                r.displacement === row.displacement &&
                                r.torque === row.torque
                              );
                            }
                          });
                          const isDisabled =
                            !isChecked && (selectedRows?.length ?? 0) >= 3;

                          return (
                            <input
                              type="checkbox"
                              checked={isChecked}
                              disabled={isDisabled}
                              style={{
                                accentColor: "black",
                                cursor: isDisabled ? "not-allowed" : "pointer",
                              }}
                              onChange={(e) => {
                                handleRowSelection(row, e.target.checked);
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ ...stickyStyles2 }}>
                        {row.series}
                      </TableCell>
                      <TableCell sx={{ ...stickyStyles2 }}>
                        {row.displacement}
                      </TableCell>

                      {columnsToUse.slice(2).map((col) => (
                        <TableCell
                          key={col.field}
                          sx={{
                            // borderBottom: "1px solid #ddd",
                            textAlign: "center",
                            padding: "10px",
                            fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {(() => {
                            const value = row[col.field];
                            if (col.field === "dummyField") {
                              return (
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent triggering row click event
                                    const seriesEncoded = encodeURIComponent(
                                      `${row.series} Series`
                                    );
                                    const displacementEncoded =
                                      encodeURIComponent(`${row.displacement}`);
                                    const url = `https://powersource.danfoss.com/products/motors/mobile-motors/low-speed-high-torque-motors/orbital-motors/danfoss-orbital-motor-quick-selector?heroFilters.A5029_1485067=%22${seriesEncoded}%22&heroFilters.ATT_14109282=%22${displacementEncoded}%22&tab=products`;

                                    window.open(url, "_blank");
                                  }}
                                  style={{ cursor: "pointer" }}
                                  title="Open in Danfoss Quick Selector"
                                >
                                  <OpenInNewIcon
                                    style={{
                                      fontSize: "17px",
                                    }}
                                  />
                                </div>
                              );
                            }

                            if (col.field === "valveType") {
                              const [first, second] = value.split(" ");
                              return (
                                <div>
                                  {first}
                                  <br />
                                  {second}
                                </div>
                              );
                            }
                            if (col.field === "bearingType") {
                              if (
                                typeof value === "string" &&
                                value.includes(" ")
                              ) {
                                const [first, second] = value.split(" ");
                                return (
                                  <div>
                                    {first}
                                    <br />
                                    {second}
                                  </div>
                                );
                              }
                              return value;
                            }
                            if (
                              ["torque", "pressure", "flow", "speed"].includes(
                                col.field
                              )
                            ) {
                              return Math.round(value);
                            }
                            if (col.field === "efficiency") {
                              return `${Math.round(parseFloat(value) * 100)}`;
                            }
                            if (col.field === "durabilityIndex") {
                              return `${Math.round(parseFloat(value))}`;
                            }
                            return value;
                          })()}
                        </TableCell>
                      ))}
                    </TableRow>
                  </React.Fragment>
                ))}
            </TableBody>
          </Table>
          {/* <TablePagination
            rowsPerPageOptions={[10, 20, 30, 50]}
            component="div"
            count={filteredData.length} // Total rows
            rowsPerPage={rowsPerPage} // Rows per page
            page={page} // Current page
            onPageChange={handleChangePage} // Handle page change
            onRowsPerPageChange={handleChangeRowsPerPage} // Handle rows per page change
          /> */}
        </TableContainer>
      </div>
    </>
  );
};

export default TableDataContainer;
